import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Activity,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  Plus,
  RefreshCw,
  Satellite,
  Save,
  Trash2,
} from 'lucide-react';

import { api, gpsProvidersAPI } from '../lib/api';
import { messageErreur } from '../lib/erreurs';

import { formatDateHeure } from '../lib/formats';
import { libelle, texteSur } from '../lib/libelles';
import { Badge } from '../components/ui/badge';
import { Aide } from '../components/ui/aide';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SectionCard } from '../components/dashboard';
import { PageHero } from '../components/dashboard/PageHero';
import { isDesktop, publicOrigin } from '../lib/desktop';

// Réglages dont l'absence rend une fiche « pont Traccar » inerte, en mots simples.
const REGLAGE_MANQUANT = {
  name: 'le nom',
  provider_key: 'la clé du fournisseur',
  organization_id: 'la société',
  instance_secret: 'le secret d’instance',
  device_unique_ids: 'la liste des boîtiers (IMEI)',
  previous_secret_expires_at: 'la date de fin de l’ancien secret',
};

/**
 * Fournisseurs GPS — brancher les boîtiers qui remontent la position des camions.
 *
 * L'écran se lit de haut en bas dans l'ordre où l'installation se fait : on
 * déclare le fournisseur, on récupère l'URL à lui donner, puis on constate ce
 * qui est déjà branché. C'est l'inverse de la version précédente, qui posait
 * côte à côte un formulaire étroit et un mur d'explications — la personne qui
 * configure un boîtier pour la première fois ne savait pas par où commencer.
 *
 * La couleur ne marque que des états : une fiche inactive, un secret absent,
 * une configuration incomplète, un diagnostic. Rien n'est coloré parce que
 * c'est « la carte du haut » ou « le bouton de droite ».
 */

// Un secret d'instance Traccar autorise l'écriture de positions pour tous les
// camions déclarés sur sa fiche : court, il se devine. Le backend refuse en
// dessous de cette longueur, autant le dire avant l'aller-retour.
const MIN_INSTANCE_SECRET_LENGTH = 24;

const initialForm = {
  name: '',
  provider_key: '',
  provider_type: 'generic_webhook',
  auth_mode: 'shared_secret',
  shared_secret: '',
  base_url: '',
  polling_interval_seconds: 60,
  api_token: '',
  instance_secret: '',
  device_unique_ids: '',
  require_device_secret: false,
  is_active: true,
};

// Un `uniqueId` par ligne, ou collés séparés par des virgules : l'installateur
// exporte sa liste de boîtiers, il ne la ressaisit pas.
const parseDeviceIds = (raw) =>
  String(raw || '')
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);

// Un diagnostic n'émet aucun appel sortant : ces libellés décrivent ce qui est
// réellement constaté, pour qu'un boîtier jamais branché ne se lise pas comme
// un boîtier en panne.
const diagnosticBadge = {
  receiving: { label: 'Positions reçues', variant: 'success' },
  no_point_yet: { label: 'Aucun point reçu à ce jour', variant: 'warning' },
  silent: { label: 'Plus aucun point', variant: 'warning' },
  incomplete_configuration: { label: 'Configuration incomplète', variant: 'warning' },
  unsupported_type: { label: 'Intégration non disponible', variant: 'destructive' },
};

/**
 * Un champ étiqueté avec sa phrase d'aide.
 *
 * L'aide est sous le champ et non dans le `placeholder` : un placeholder
 * disparaît à la première frappe, or c'est précisément au moment où l'on tape
 * qu'on se demande ce qui est attendu.
 */
const Champ = ({ id, label, aide, detail, children }) => {
  // Une aide de champ de huit mots ou moins reste sous le champ. Au-delà, elle
  // passe derrière une icône, sauf la mention de sécurité « jamais réaffiché ».
  const mots = typeof aide === 'string' ? aide.trim().split(/\s+/).length : 0;
  const aideCourte = aide && (mots <= 8 || /réaffiché/.test(aide));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <Label htmlFor={id} className="text-foreground">
          {label}
        </Label>
        {aide && !aideCourte ? <Aide titre={typeof label === 'string' ? label : 'Aide'}>{aide}</Aide> : null}
        {detail ? <Aide titre={typeof label === 'string' ? label : 'Aide'}>{detail}</Aide> : null}
      </div>
      {children}
      {aideCourte ? <p className="text-xs text-muted-foreground">{aide}</p> : null}
    </div>
  );
};

/**
 * Un champ de secret, masqué par défaut, avec une bascule pour le relire.
 *
 * Masqué : ces écrans se remplissent souvent à deux, l'installateur dictant le
 * code par-dessus l'épaule, et un secret laissé en clair part avec la capture
 * d'écran envoyée au support. Révélable quand même : un secret qu'on ne peut
 * pas relire se ressaisit à l'aveugle, donc mal, et une faute de frappe sur un
 * secret ne se voit qu'au moment où plus aucune position n'arrive.
 *
 * La valeur ne sort jamais d'ici : ni journal, ni URL, ni paramètre de requête.
 */
const ChampSecret = ({ id, name, value, onChange, placeholder, required }) => {
  const [revele, setRevele] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        name={name}
        type={revele ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        // Ni proposition ni mémorisation par le navigateur : ce secret
        // appartient à l'organisation, pas au poste de travail. « off » est
        // ignoré par les navigateurs sur un champ de mot de passe, qui y
        // collent le mot de passe enregistré de la personne ; « new-password »
        // leur dit qu'il s'agit d'un nouveau secret, à ne pas pré-remplir.
        autoComplete="new-password"
        data-1p-ignore="true"
        data-lpignore="true"
        spellCheck="false"
        className="pr-11 font-mono"
      />
      <button
        type="button"
        onClick={() => setRevele((etat) => !etat)}
        aria-label={revele ? 'Masquer le secret' : 'Afficher le secret'}
        aria-pressed={revele}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {revele ? (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
};

export default function GpsProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [providerHealth, setProviderHealth] = useState({});
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [rotation, setRotation] = useState({ providerId: null, secret: '' });
  const [rotationRevelee, setRotationRevelee] = useState(false);
  // Quelle fenêtre est ouverte : le formulaire d'ajout, les adresses, ou aucune.
  const [fenetre, setFenetre] = useState(null);

  /**
   * L'origine sur laquelle le fournisseur devra pousser ses positions.
   *
   * Elle est déduite, jamais écrite en dur : le même code tourne sur
   * l'installation d'un client, sur une préproduction et sur un poste de
   * développement, et une URL codée en dur ferait envoyer les positions d'un
   * client chez un autre. `REACT_APP_BACKEND_URL` fait foi quand le build la
   * porte — c'est elle que sert l'API, et elle seule accepte le webhook — sinon
   * on retombe sur l'origine réellement servie au navigateur.
   */
  const ingestionOrigin = useMemo(() => {
    const configuree = (process.env.REACT_APP_BACKEND_URL || '').trim();
    if (configuree) return configuree.replace(/\/+$/, '');
    if (typeof window === 'undefined') return '';
    // Application Windows: l'origine de la page (app://) n'est joignable par aucun fournisseur.
    if (isDesktop()) return publicOrigin();
    // En développement le front est servi à part du backend : l'origine du
    // navigateur pointe alors sur le serveur de développement, pas sur l'API.
    return window.location.origin.replace(':5173', ':8001');
  }, []);

  const webhookUrl = useMemo(
    () => `${ingestionOrigin}/api/v1/tracking/hardware/gps`,
    [ingestionOrigin],
  );
  const traccarUrl = useMemo(() => `${webhookUrl}/traccar`, [webhookUrl]);

  const load = async () => {
    setProviders((await gpsProvidersAPI.getAll()) || []);
  };

  useEffect(() => {
    load().catch(() => toast.error('Impossible de charger les fournisseurs GPS'));
  }, []);

  const isPolling = form.provider_type === 'vendor_polling';
  const isBridge = form.provider_type === 'traccar_bridge';

  const buildPayload = () => {
    // Une interrogation ne présente pas de secret partagé : c'est elle qui
    // s'authentifie auprès du serveur distant. Envoyer les deux jeux de
    // champs ferait afficher « configuration incomplète » à une fiche saine.
    if (isPolling) {
      return {
        name: form.name,
        provider_key: form.provider_key,
        provider_type: 'vendor_polling',
        auth_mode: 'api_key',
        is_active: form.is_active,
        base_url: form.base_url,
        polling_interval_seconds: Number(form.polling_interval_seconds) || 60,
        config_json: { vendor: 'traccar', api_token: form.api_token },
      };
    }
    if (isBridge) {
      // Pas de base_url : un pont ne s'interroge pas, il pousse. Le backend
      // refuse la fiche si elle en porte une, pour qu'elle ne soit pas reprise
      // par la tâche de polling.
      return {
        name: form.name,
        provider_key: form.provider_key,
        provider_type: 'traccar_bridge',
        auth_mode: 'shared_secret',
        is_active: form.is_active,
        config_json: {
          instance_secret: form.instance_secret,
          device_unique_ids: parseDeviceIds(form.device_unique_ids),
          require_device_secret: form.require_device_secret,
        },
      };
    }
    return {
      name: form.name,
      provider_key: form.provider_key,
      provider_type: 'generic_webhook',
      auth_mode: form.auth_mode,
      is_active: form.is_active,
      config_json: form.shared_secret ? { shared_secret: form.shared_secret } : {},
    };
  };

  const save = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await gpsProvidersAPI.create(buildPayload());
      setForm(initialForm);
      setFenetre(null);
      await load();
      toast.success('Fournisseur GPS enregistré');
    } catch (error) {
      // Le backend refuse une fiche derrière laquelle il n'y a aucun code, et
      // dit pourquoi. Avaler ce message laisserait croire à un échec réseau.
      toast.error(messageErreur(error, "Le fournisseur GPS n'a pas pu être enregistré"));
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (provider) => {
    await gpsProvidersAPI.update(provider.id, { is_active: !provider.is_active });
    await load();
  };

  const runDiagnostic = async (provider) => {
    const result = await gpsProvidersAPI.diagnostics(provider.id);
    setProviderHealth((current) => ({ ...current, [provider.id]: result }));
    const level = result.status === 'success' ? 'success' : result.status === 'error' ? 'error' : 'warning';
    toast[level](texteSur(result.message, 'Diagnostic effectué'));
  };

  const remove = async (provider) => {
    await gpsProvidersAPI.delete(provider.id);
    await load();
    toast.success('Fournisseur supprimé');
  };

  // La rotation est faite côté serveur : l'API ne rend jamais le secret en
  // clair, donc l'écran ne peut pas reconstruire la configuration lui-même. Il
  // envoie l'entrant, le serveur garde le sortant valide le temps de la fenêtre.
  const rotate = async (provider) => {
    if (rotation.secret.length < MIN_INSTANCE_SECRET_LENGTH) {
      toast.error(`Le nouveau secret doit faire au moins ${MIN_INSTANCE_SECRET_LENGTH} caractères.`);
      return;
    }
    try {
      const { data } = await api.post(`/tracking/providers/${provider.id}/rotate-secret`, {
        instance_secret: rotation.secret,
        grace_period_hours: 24,
      });
      setRotation({ providerId: null, secret: '' });
      setRotationRevelee(false);
      await load();
      toast.success(texteSur(data?.message, 'Secret d’instance remplacé'));
    } catch (error) {
      toast.error(messageErreur(error, 'Le secret n’a pas pu être remplacé'));
    }
  };

  // Le presse-papiers peut être refusé (contexte non sécurisé, permission) :
  // le dire vaut mieux qu'un bouton qui ne fait rien, puisque l'URL reste
  // sélectionnable à la main.
  const copier = async (valeur, nomAdresse) => {
    try {
      await navigator.clipboard.writeText(valeur);
      toast.success(`${nomAdresse} copiée`);
    } catch {
      toast.error('Copie impossible : sélectionnez l’URL et copiez-la à la main.');
    }
  };

  const ouvrirFormulaire = () => setFenetre('formulaire');
  const ouvrirAdresses = () => setFenetre('adresses');
  const fermerFenetre = (ouverte) => {
    if (!ouverte) setFenetre(null);
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHero
        ton="calme"
        illustration="suivi"
        eyebrow="Réglages"
        title="Fournisseurs GPS"
        stats={[
          { label: providers.length > 1 ? 'Fournisseurs' : 'Fournisseur', valeur: providers.length },
          {
            label: 'Actifs',
            valeur: providers.filter((provider) => provider.is_active).length,
            ton: 'positif',
          },
        ]}
        description="Connectez vos boîtiers GPS pour recevoir automatiquement les positions de vos camions."
        actions={
          <>
            <Button variant="outline" onClick={load}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Rafraîchir
            </Button>
            <Button variant="outline" onClick={ouvrirAdresses}>
              <Link2 className="h-4 w-4" aria-hidden="true" />
              Adresses de réception
            </Button>
            <Button onClick={ouvrirFormulaire}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Ajouter un fournisseur
            </Button>
          </>
        }
      />

      <SectionCard
        icon={Satellite}
        title="Fournisseurs configurés"
        subtitle={
          providers.length
            ? `${providers.length} fournisseur(s) branché(s) sur votre société.`
            : 'Aucune fiche pour le moment.'
        }
      >
        {providers.length === 0 ? (
          <EmptyState
            icon={Satellite}
            title="Aucun fournisseur GPS configuré"
            description="Ajoutez votre premier fournisseur pour commencer à recevoir les positions de vos camions."
            className="border-0 bg-transparent"
            action={
              <Button onClick={ouvrirFormulaire}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Ajouter un fournisseur
              </Button>
            }
          />
        ) : (
          <div className="grid gap-3">
            {providers.map((provider) => {
              const support = provider.integration_support;
              const health = providerHealth[provider.id];
              const badge = health ? diagnosticBadge[health.state] : null;
              const config = provider.config_json || {};
              const isBridgeCard = provider.provider_type === 'traccar_bridge';
              const declaredCount = (config.device_unique_ids || []).length;
              const rotationEndsAt = config.previous_secret_expires_at;
              const missing = provider.missing_configuration || [];
              const enRotation = rotation.providerId === provider.id;

              return (
                <article
                  key={provider.id}
                  className="rounded-xl border border-border/70 bg-card p-4 shadow-sm"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-bold text-foreground">{provider.name}</h3>
                        {/* Actif / inactif est un état, et c'est le seul de la
                            ligne qui mérite une couleur pleine. */}
                        <Badge variant={provider.is_active ? 'success' : 'muted'}>
                          {provider.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                        <Badge variant={support && !support.supported ? 'destructive' : 'outline'}>
                          {libelle('gpsProvider', provider.provider_type)}
                        </Badge>
                      </div>

                      <p className="text-sm text-muted-foreground">
                        Clé <code className="font-mono">{provider.provider_key}</code> · Accès :{' '}
                        {libelle('gpsAuthMode', provider.auth_mode)}
                      </p>

                      {support && !support.supported && (
                        <p className="text-xs text-destructive">{texteSur(support.reason, 'Ce type de fournisseur n’est pas pris en charge.')}</p>
                      )}

                      {isBridgeCard && (
                        <div className="space-y-1 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={declaredCount ? 'outline' : 'warning'}>
                              {declaredCount} boîtier(s) déclaré(s)
                            </Badge>
                            <Badge variant={config.instance_secret_set ? 'outline' : 'warning'}>
                              {config.instance_secret_set ? 'Secret en place' : 'Sans secret'}
                            </Badge>
                            {config.require_device_secret && (
                              <Badge variant="outline">Secret par boîtier exigé</Badge>
                            )}
                          </div>
                          {rotationEndsAt && (
                            <p className="text-muted-foreground">
                              Changement de secret en cours : l'ancien secret reste accepté jusqu'au{' '}
                              {formatDateHeure(rotationEndsAt)}. Reportez le
                              nouveau dans l’envoi de Traccar avant cette date.
                            </p>
                          )}
                          {missing.length > 0 && missing.every((m) => m === 'device_unique_ids') && (
                            // Aucun boîtier déclaré : c'est l'état normal avant d'équiper un
                            // camion, pas une erreur. On dit quoi faire, sans rouge.
                            <p className="text-muted-foreground">
                              Aucun boîtier déclaré pour l’instant.{' '}
                              <Aide titre="Aucun boîtier déclaré">
                                Ajoutez l’IMEI d’un boîtier ici quand vous équiperez un camion : ses
                                positions seront alors acceptées.
                              </Aide>
                            </p>
                          )}
                          {missing.some((m) => m !== 'device_unique_ids') && (
                            // Une fiche incomplète est inerte, donc silencieuse : le dire ici
                            // évite d'attendre des points qui ne viendront jamais.
                            <p className="text-destructive">
                              Configuration incomplète : {missing.map((m) => REGLAGE_MANQUANT[m] || 'un réglage').join(', ')}. Aucune position ne
                              sera acceptée pour ses boîtiers.
                            </p>
                          )}
                        </div>
                      )}

                      {health && (
                        <div className="space-y-1 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
                            <span className="text-muted-foreground">
                              {health.telemetry_points || 0} point(s) reçus
                            </span>
                          </div>
                          <p className="text-muted-foreground">{texteSur(health.message, '')}</p>
                          <p className="text-muted-foreground/80">{texteSur(health.scope, '')}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
                      <Button variant="outline" size="sm" onClick={() => runDiagnostic(provider)}>
                        <Activity className="h-4 w-4" aria-hidden="true" />
                        Diagnostic
                      </Button>
                      {isBridgeCard && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRotationRevelee(false);
                            setRotation(
                              enRotation
                                ? { providerId: null, secret: '' }
                                : { providerId: provider.id, secret: '' },
                            );
                          }}
                        >
                          <KeyRound className="h-4 w-4" aria-hidden="true" />
                          Changer le secret
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => toggle(provider)}>
                        {provider.is_active ? 'Désactiver' : 'Activer'}
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => remove(provider)}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                        Supprimer
                      </Button>
                    </div>
                  </div>

                  {enRotation && (
                    <div className="mt-4 space-y-2 rounded-lg border border-dashed border-border bg-muted/40 p-3">
                      <Label htmlFor={`rotation-${provider.id}`} className="text-foreground">
                        Nouveau secret d'instance
                      </Label>
                      {/* Le secret de rotation est aussi sensible que celui de
                          création : même masquage, même bascule. */}
                      <div className="relative">
                        <Input
                          id={`rotation-${provider.id}`}
                          name="gps_nouveau_secret_instance"
                          type={rotationRevelee ? 'text' : 'password'}
                          autoComplete="new-password"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          spellCheck="false"
                          className="bg-card pr-11 font-mono"
                          placeholder={`${MIN_INSTANCE_SECRET_LENGTH} caractères minimum`}
                          value={rotation.secret}
                          onChange={(e) => setRotation({ ...rotation, secret: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setRotationRevelee((etat) => !etat)}
                          aria-label={rotationRevelee ? 'Masquer le secret' : 'Afficher le secret'}
                          aria-pressed={rotationRevelee}
                          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {rotationRevelee ? (
                            <EyeOff className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <Eye className="h-4 w-4" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={() => rotate(provider)}>
                          <KeyRound className="h-4 w-4" aria-hidden="true" />
                          Remplacer le secret
                        </Button>
                        <Aide titre="Remplacer le secret">
                          L'ancien secret reste accepté 24 h : la remontée ne coupe pas pendant que
                          vous mettez à jour le secret dans Traccar et redémarrez le serveur.
                        </Aide>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>

      <Dialog open={fenetre === 'formulaire'} onOpenChange={fermerFenetre}>
        <DialogContent className="max-w-3xl">
          <DialogHeader className="pr-6">
            <DialogTitle>Ajouter un fournisseur</DialogTitle>
            <DialogDescription>
              Configurez un fournisseur GPS pour récupérer les positions de vos camions.
            </DialogDescription>
          </DialogHeader>
        <form onSubmit={save} className="space-y-5" autoComplete="off">
          <div className="grid gap-4 md:grid-cols-2 md:gap-5">
            {/* Colonne gauche : l'identité de la fiche, la même quel que soit
                le type d'intégration choisi à droite. */}
            <div className="space-y-4">
              <Champ
                id="gps-nom"
                label="Nom visible *"
                aide="Nom qui sera affiché dans la liste de vos fournisseurs."
              >
                <Input
                  id="gps-nom"
                  name="gps_fournisseur_nom"
                  autoComplete="off"
                  placeholder="Ex. Teltonika"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Champ>

              <Champ
                id="gps-cle"
                label="Clé du fournisseur *"
                aide="Nom court sans espace qui identifie ce fournisseur (par exemple boitiers-mauritanie)."
              >
                <Input
                  id="gps-cle"
                  name="gps_fournisseur_cle"
                  autoComplete="off"
                  spellCheck="false"
                  className="font-mono"
                  placeholder="Ex. boitiers-mauritanie"
                  value={form.provider_key}
                  onChange={(e) => setForm({ ...form, provider_key: e.target.value })}
                  required
                />
              </Champ>
            </div>

            {/* Colonne droite : le type d'intégration, puis ce qu'il exige pour
                s'authentifier. Les trois branches ne demandent pas la même
                chose — les afficher toutes ferait remplir des champs inertes. */}
            <div className="space-y-4">
              <Champ
                id="gps-type"
                label="Type d'intégration *"
                detail={
                  isPolling ? (
                    <>
                      La plateforme va lire les positions sur ce serveur Traccar et rattache chaque
                      position au camion par le numéro de son boîtier (IMEI). Seul Traccar peut être lu :
                      aucun autre fournisseur n'est branché, et une fiche qui en désignerait un serait
                      refusée. La lecture automatique doit être activée par l’administrateur du serveur.
                    </>
                  ) : !isBridge ? (
                    <>
                      Le boîtier envoie ses positions à l'adresse ci-dessous, accompagnées du secret du
                      camion. Le secret attendu est celui enregistré sur la fiche du camion, pas celui saisi
                      ici : un camion sans secret refuse toutes les positions.
                    </>
                  ) : null
                }
              >
                <Select
                  value={form.provider_type}
                  onValueChange={(value) => setForm({ ...form, provider_type: value })}
                >
                  <SelectTrigger id="gps-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="generic_webhook">Réception directe — le boîtier envoie ses positions</SelectItem>
                    <SelectItem value="traccar_bridge">Pont Traccar — un serveur Traccar renvoie les positions</SelectItem>
                    <SelectItem value="vendor_polling">Lecture sur un serveur Traccar</SelectItem>
                  </SelectContent>
                </Select>
              </Champ>

              {isBridge ? (
                <Champ
                  id="gps-secret-instance"
                  label="Secret d'instance *"
                  detail={
                    <>
                      Ce secret est celui que le serveur Traccar joint à chaque envoi. Il
                      n'ouvre que les <strong className="text-foreground">camions de cette société</strong>{' '}
                      dont le numéro de boîtier est déclaré ci-dessous : un secret divulgué ne peut rien
                      pour un autre client, ni pour un boîtier non déclaré. Il n'est jamais réaffiché —
                      notez-le de votre côté, et utilisez « Changer le secret » pour le remplacer.
                    </>
                  }
                  aide={`Au moins ${MIN_INSTANCE_SECRET_LENGTH} caractères. Il ne sera jamais réaffiché : notez-le de votre côté.`}
                >
                  <ChampSecret
                    id="gps-secret-instance"
                    name="gps_secret_instance"
                    placeholder={`${MIN_INSTANCE_SECRET_LENGTH} caractères minimum`}
                    value={form.instance_secret}
                    onChange={(e) => setForm({ ...form, instance_secret: e.target.value })}
                    required
                  />
                </Champ>
              ) : isPolling ? (
                <>
                  <Champ
                    id="gps-base-url"
                    label="URL du serveur Traccar *"
                    aide="Adresse du serveur Traccar que la plateforme ira lire."
                  >
                    <Input
                      id="gps-base-url"
                      name="gps_serveur_traccar_url"
                      autoComplete="off"
                      spellCheck="false"
                      className="font-mono"
                      placeholder="Ex. https://traccar.exemple.com"
                      value={form.base_url}
                      onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                      required
                    />
                  </Champ>
                  <Champ
                    id="gps-token"
                    label="Jeton d’accès Traccar"
                    aide="Ce jeton permet de se connecter au serveur Traccar distant."
                  >
                    <ChampSecret
                      id="gps-token"
                      name="gps_jeton_traccar"
                      placeholder="Jeton d’accès"
                      value={form.api_token}
                      onChange={(e) => setForm({ ...form, api_token: e.target.value })}
                    />
                  </Champ>
                  <Champ
                    id="gps-intervalle"
                    label="Fréquence de lecture"
                    aide="En secondes, 15 au minimum."
                  >
                    <Input
                      id="gps-intervalle"
                      name="gps_frequence_lecture"
                      autoComplete="off"
                      type="number"
                      min="15"
                      placeholder="60"
                      value={form.polling_interval_seconds}
                      onChange={(e) =>
                        setForm({ ...form, polling_interval_seconds: e.target.value })
                      }
                    />
                  </Champ>
                </>
              ) : (
                <Champ
                  id="gps-secret"
                  label="Secret partagé *"
                  aide="Ce secret sera utilisé pour valider les données envoyées par le fournisseur."
                >
                  <div className="space-y-2">
                    {/* Le mode d'authentification précède le secret : il décide
                        de ce que le champ du dessous représente. */}
                    <Select
                      value={form.auth_mode}
                      onValueChange={(value) => setForm({ ...form, auth_mode: value })}
                    >
                      <SelectTrigger aria-label="Mode d'authentification">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shared_secret">Secret partagé</SelectItem>
                        <SelectItem value="api_key">Clé d’accès</SelectItem>
                        <SelectItem value="none">Sans authentification</SelectItem>
                      </SelectContent>
                    </Select>
                    <ChampSecret
                      id="gps-secret"
                      name="gps_secret_partage"
                      placeholder="Secret partagé ou clé d’accès"
                      value={form.shared_secret}
                      onChange={(e) => setForm({ ...form, shared_secret: e.target.value })}
                    />
                  </div>
                </Champ>
              )}
            </div>
          </div>

          {/* Ce que seules certaines intégrations demandent, et qui ne tient pas
              dans une colonne : la liste des boîtiers, et l'explication du
              modèle de confiance. */}
          {isBridge && (
            <div className="space-y-4 rounded-xl border border-border/70 bg-muted/40 p-4">
              <Champ
                id="gps-boitiers"
                label="Boîtiers déclarés"
                aide="Un numéro d’identification (IMEI) par ligne. Seuls ces boîtiers seront acceptés."
              >
                <Textarea
                  id="gps-boitiers"
                  name="gps_boitiers_imei"
                  autoComplete="off"
                  className="min-h-[110px] bg-card font-mono text-xs"
                  placeholder={'352093081452251\n860123456789012'}
                  value={form.device_unique_ids}
                  onChange={(e) => setForm({ ...form, device_unique_ids: e.target.value })}
                />
              </Champ>

              <label className="flex items-start gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  name="gps_secret_par_boitier"
                  className="mt-0.5 accent-primary"
                  checked={form.require_device_secret}
                  onChange={(e) => setForm({ ...form, require_device_secret: e.target.checked })}
                />
                <span>Exiger un secret par boîtier.</span>
                <Aide titre="Secret par boîtier" className="-mt-1">
                  Le secret d'instance ne suffira plus pour ces
                  boîtiers : Traccar devra présenter le secret propre à chaque camion, ce qui
                  demande une adresse d’envoi par boîtier (voir la procédure d’installation GPS).
                </Aide>
              </label>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setFenetre(null)}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={
                loading ||
                !form.name ||
                !form.provider_key ||
                (isPolling && !form.base_url) ||
                (isBridge && form.instance_secret.length < MIN_INSTANCE_SECRET_LENGTH)
              }
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              Enregistrer le fournisseur
            </Button>
          </div>
        </form>
        </DialogContent>
      </Dialog>

      <Dialog open={fenetre === 'adresses'} onOpenChange={fermerFenetre}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="pr-6">
            <div className="flex items-center gap-2">
              <DialogTitle>Adresses de réception</DialogTitle>
              <Aide titre="Boîtiers Teltonika, Concox, Queclink, Coban" align="start">
                <div className="space-y-2">
                  <p>
                    Ces boîtiers parlent un langage que ces adresses ne comprennent pas
                    directement. Deux voies existent : un serveur Traccar qui décode leurs trames
                    et les renvoie ici, ou l’écoute directe de la plateforme (Teltonika et Concox),
                    désactivée par défaut et à réserver à un réseau privé. Demandez la procédure
                    d’installation à votre administrateur.
                  </p>
                  <p>
                    <strong className="text-foreground">
                      Une fiche « pont Traccar » par serveur Traccar.
                    </strong>{' '}
                    Sans elle, le secret du serveur ouvre tous les camions non déclarés, toutes
                    sociétés confondues. Avec elle, il ne vaut plus que pour les boîtiers qu’elle
                    déclare, et seulement pour les camions de votre société.
                  </p>
                </div>
              </Aide>
            </div>
            <DialogDescription>
              Donnez ces adresses à votre fournisseur GPS. Elles sont propres à votre société :
              ne les partagez qu’avec lui.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4">
              <Champ
                id="gps-url-webhook"
                label="Réception directe des positions"
                aide="Adresse principale pour recevoir les positions GPS."
              >
                <div className="flex gap-2">
                  <Input
                    id="gps-url-webhook"
                    name="gps_adresse_reception"
                    autoComplete="off"
                    readOnly
                    value={webhookUrl}
                    onFocus={(e) => e.target.select()}
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => copier(webhookUrl, 'Adresse de réception directe')}
                  >
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Copier
                  </Button>
                </div>
              </Champ>

              <Champ
                id="gps-url-traccar"
                label="Pont Traccar"
                aide="Adresse à donner à un serveur Traccar qui décode les trames des boîtiers."
              >
                <div className="flex gap-2">
                  <Input
                    id="gps-url-traccar"
                    name="gps_adresse_traccar"
                    autoComplete="off"
                    readOnly
                    value={traccarUrl}
                    onFocus={(e) => e.target.select()}
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => copier(traccarUrl, 'Adresse du pont Traccar')}
                  >
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Copier
                  </Button>
                </div>
              </Champ>
            </div>
            </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
